import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Plus } from 'lucide-react';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { AdditionalQuestionsSection } from './components/AdditionalQuestionsSection';
import { ControllingOwnerCard } from './components/ControllingOwnerCard';
import { ControllingOwnerTypeSection } from './components/ControllingOwnerTypeSection';
import { OriginOfAssetsSection } from './components/OriginOfAssetsSection';
import { useFormKData } from './hooks/useFormKData';
import type { FormKHandle, FormKProps } from './types/formK.types';
import { getDefaultControllingOwnerValues } from './utils/formK.utils';

/**
 * FormK Component - Controlling Owners Form
 *
 * This form collects information about controlling owners for KYC compliance.
 * It has been refactored for better maintainability with extracted components,
 * hooks, and utilities.
 */
export const FormK = forwardRef<FormKHandle, FormKProps>(function FormK(
  { onSubmit, onError, onChange, className, defaultValues },
  ref
) {
  const { t: tForms } = useFormsTranslation();

  // Initialize form with custom hook
  const { form, submitError, handleSubmit } = useFormKData(
    tForms,
    defaultValues,
    onSubmit,
    onError,
    onChange
  );

  // Field array for controlling owners
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'controllingOwners',
  });

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    triggerValidation: async () => {
      const isValid = await form.trigger();
      return isValid;
    },
  }));

  // Watch origin of assets for conditional rendering
  const watchOriginOfAssets = useWatch({
    control: form.control,
    name: 'originOfAssets',
  });

  // Memoize default values for new controlling owner
  const defaultControllingOwnerValues = useMemo(
    () => getDefaultControllingOwnerValues(),
    []
  );

  return (
    <div className={className}>
      {submitError && (
        <Alert variant='destructive' className='mb-6'>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          id='form-k-form'
          onSubmit={form.handleSubmit(handleSubmit)}
          className='space-y-6'
          aria-label={tForms('formK.title')}
        >
          {/* Form Header */}
          <div className='text-center mb-6'>
            <h1 className='text-2xl font-bold mb-2'>{tForms('formK.title')}</h1>
          </div>

          {/* 1. Origin of Assets Section */}
          <OriginOfAssetsSection
            control={form.control}
            watchOriginOfAssets={watchOriginOfAssets}
            setValue={form.setValue}
            getValues={form.getValues}
          />

          {/* 2. Determination of the Controlling Owner */}
          <section
            className='space-y-6'
            aria-labelledby='controlling-owner-heading'
          >
            <div className='flex items-center gap-2 mb-4'>
              <h3
                id='controlling-owner-heading'
                className='text-lg font-semibold'
              >
                {tForms('formK.sections.controllingOwner')}
              </h3>
            </div>

            {/* Controlling Owner Type Selection */}
            <ControllingOwnerTypeSection control={form.control} />

            {/* Controlling Owners List */}
            <div
              className='space-y-4'
              aria-labelledby='controlling-owner-details-heading'
            >
              <div className='flex items-center justify-between'>
                <h4
                  id='controlling-owner-details-heading'
                  className='text-md font-medium'
                >
                  {tForms('formK.sections.controllingOwnerDetails')}
                </h4>
                {form.formState.errors.controllingOwners?.message && (
                  <span
                    className='text-sm text-destructive'
                    role='alert'
                    aria-live='polite'
                  >
                    {form.formState.errors.controllingOwners.message}
                  </span>
                )}
              </div>

              {fields.map((field, index) => (
                <ControllingOwnerCard
                  key={field.id}
                  control={form.control}
                  index={index}
                  onRemove={() => remove(index)}
                  canRemove={fields.length > 1}
                />
              ))}

              <Button
                type='button'
                variant='outline'
                onClick={() => append(defaultControllingOwnerValues)}
                className='w-full'
              >
                <Plus className='w-4 h-4 mr-2' />
                {tForms('formK.buttons.addControllingOwner')}
              </Button>
            </div>
          </section>

          {/* 3. Additional Questions Section */}
          <AdditionalQuestionsSection control={form.control} />
        </form>
      </Form>
    </div>
  );
});

export default FormK;
